import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { formatEUR, statusColor, statusDot } from "@/lib/format";
import { useLang } from "@/i18n";
import { Reveal } from "./Reveal";

const filterKeys = ["Tutte", "Conclusa", "In corso", "In arrivo"];
const filterTestIds = ["portfolio-filter-all", "portfolio-filter-concluse", "portfolio-filter-in-corso", "portfolio-filter-in-arrivo"];

export const Portfolio = () => {
  const [operations, setOperations] = useState([]);
  const [filter, setFilter] = useState("Tutte");
  const { t, lang } = useLang();
  const filterLabels = t("filters");

  useEffect(() => {
    api.get("/operations", { params: { lang } }).then((r) => setOperations(r.data)).catch(() => {});
  }, [lang]);

  const visible = filter === "Tutte" ? operations : operations.filter((o) => o.status === filter);

  return (
    <section id="operazioni" className="py-20 lg:py-32 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
      <Reveal>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div>
            <p className="text-xs tracking-[0.35em] uppercase font-mono text-amber-400/90 mb-4">{t("port_overline")}</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-zinc-100 max-w-xl">
              {t("port_h1")} <span className="italic text-gold">{t("port_h2")}</span>
            </h2>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="portfolio-filters">
            {filterKeys.map((key, i) => (
              <button
                key={key}
                data-testid={filterTestIds[i]}
                onClick={() => setFilter(key)}
                className={`text-[11px] tracking-[0.2em] uppercase px-5 py-2.5 border transition-colors duration-300 ${
                  filter === key
                    ? "border-[#D4AF37] text-[#D4AF37] bg-amber-500/10"
                    : "border-white/10 text-zinc-500 hover:border-amber-500/40 hover:text-zinc-300"
                }`}
              >
                {filterLabels[i]}
              </button>
            ))}
          </div>
        </div>
      </Reveal>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8 mt-14">
        {visible.map((op, i) => {
          const wide = i % 3 === 0;
          return (
            <Reveal key={op.id} delay={(i % 3) * 0.12} className={wide ? "md:col-span-7" : "md:col-span-5"}>
              <Link
                to={`/operazioni/${op.id}`}
                data-testid="operation-card-item"
                aria-label={op.title}
                className="group relative block cursor-pointer card-lift"
              >
                <div className="absolute -inset-0 translate-x-3 translate-y-3 border border-amber-500/15 -z-10 transition-transform duration-500 group-hover:translate-x-1.5 group-hover:translate-y-1.5" />
                <div className={`relative overflow-hidden bg-[#121216] ${wide ? "aspect-[16/10]" : "aspect-[4/3]"}`}>
                  <img
                    src={op.image_url}
                    alt={op.title}
                    loading="lazy"
                    className="w-full h-full object-cover saturate-[0.65] transition-[transform,saturate] duration-700 ease-out group-hover:scale-105 group-hover:saturate-100"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C]/85 via-transparent to-transparent" />
                  <span
                    className={`absolute top-4 left-4 flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase font-mono border px-3 py-1.5 backdrop-blur ${statusColor(op.status)}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${statusDot(op.status)}`} />
                    {op.status}
                  </span>
                  <span className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center border border-white/15 bg-[#0A0A0C]/50 backdrop-blur text-zinc-300 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <ArrowUpRight size={16} />
                  </span>
                  <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
                    <p className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase font-mono text-amber-400/80">
                      <MapPin size={11} /> {op.location}
                    </p>
                    <h3 className="font-display text-xl sm:text-2xl font-semibold text-zinc-100 mt-2">{op.title}</h3>
                  </div>
                </div>
                <div className="flex items-center justify-between border border-white/10 border-t-0 bg-[#121216] px-5 sm:px-6 py-4">
                  <div>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-600">{t("card_value")}</p>
                    <p className="font-display text-lg text-gold">{formatEUR(op.value)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-600">{t("card_roi")}</p>
                    <p className="font-display text-lg text-zinc-200">{op.roi}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-600">{t("card_area")}</p>
                    <p className="font-display text-lg text-zinc-200">{Number(op.mq).toLocaleString("it-IT")} mq</p>
                  </div>
                </div>
              </Link>
            </Reveal>
          );
        })}
      </div>

      {visible.length === 0 && (
        <p className="text-zinc-500 text-sm mt-14" data-testid="portfolio-empty">{t("port_empty")}</p>
      )}
    </section>
  );
};
